import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { XMarkIcon, FolderIcon, ChevronRightIcon, UserIcon, UsersIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { toast } from 'react-toastify';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import ImageCarousel from './ImageCarousel';
import ImagePreviewModal from './ImagePreviewModal';
import FolderSelectionModal from './FolderSelectionModal';
import ContactPickerModal from './contacts/ContactPickerModal';
import NoteContextMenu from './NoteContextMenu';
import NoteShareModal from './NoteShareModal';
import { userService } from '../services/UserService';
import { Link } from 'react-router-dom';
import InlineEdit from './common/InlineEdit';
import MarkdownEditableField from './common/MarkdownEditableField';
import LinkPreview from './LinkPreview';
import { trackNotePinned, trackNoteUnpinned, trackNoteShare, trackNotePrint, trackNoteResend, trackNoteResummarize, trackNoteRetranscript } from '../analytics';
import useBreakpoint from '../hooks/useBreakpoint';

function NoteDetail({ note, onClose, onDelete, variant = 'modal' }) {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const [activeTab, setActiveTab] = useState('note');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentNote, setCurrentNote] = useState(note);
  const [previewImages, setPreviewImages] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [userData, setUserData] = useState(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
  });
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Subscribe to userService for folders and user data
  useEffect(() => {
    const updateUserData = (user) => {
      if (user) {
        setUserData(user);
        if (user.folders) {
          setFolders(user.folders);
        } else {
          setFolders([]);
        }
      } else {
        setUserData(null);
        setFolders([]);
      }
    };

    // Initial load
    updateUserData(userService.currentUser);

    // Subscribe to changes
    const unsubscribe = userService.subscribe(updateUserData);

    return unsubscribe;
  }, []);

  // Helper function to get folder name from folderId
  const getFolderName = useCallback((folderId) => {
    if (!folderId || folderId === 'inbox') return t('notes.inbox');
    if (folderId === 'archived') return t('notes.archive');
    
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.name : t('common.unknown');
  }, [folders, t]);

  useEffect(() => {
    // Clean up any empty contact IDs that might exist
    // Ensure contactIds and contacts are arrays before filtering
    const contactIds = Array.isArray(note.contactIds) ? note.contactIds : [];
    const contacts = Array.isArray(note.contacts) ? note.contacts : [];
    
    const cleanedContactIds = contactIds.filter(id => id && id.trim());
    const cleanedContacts = contacts.filter(c => c.id && c.id.trim());
    
    setCurrentNote({
        ...note,
        contactIds: cleanedContactIds,
        contacts: cleanedContacts,
        folderName: getFolderName(note.folderId) // Ensure folderName is up to date with local folders
    });
  }, [note, getFolderName]);

  const handleImagesUpdate = (newImages) => {
    setCurrentNote(prev => ({ ...prev, images: newImages }));
  };

  const handleImageClick = (index) => {
    setPreviewImages(currentNote.images);
    setPreviewIndex(index);
  };

  const handleClosePreview = () => {
    setPreviewImages(null);
    setPreviewIndex(0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return format(date, 'PPpp');
  };


  const handleContactsUpdate = async (selectedContacts) => {
    try {
        // Filter out any contacts with empty IDs
        const validContacts = selectedContacts.filter(c => c.id && c.id.trim());
        const contactIds = validContacts.map(c => c.id);
        const noteRef = doc(db, 'notes', currentNote.id);
        
        await updateDoc(noteRef, {
            contacts: validContacts,
            contactIds: contactIds,
            lastModified: serverTimestamp()
        });

        setCurrentNote(prev => ({
            ...prev,
            contacts: validContacts,
            contactIds: contactIds
        }));
        toast.success(t('notes.detail.contactsUpdated'));
    } catch (error) {
        console.error('Error updating contacts:', error);
        toast.error(t('notes.detail.failedToUpdateContacts'));
    }
  };

  const handleFolderSelect = async (folderId) => {
    try {
        const noteRef = doc(db, 'notes', currentNote.id);
        await updateDoc(noteRef, {
            folderId: folderId,
            lastModified: serverTimestamp()
        });

        const newFolderName = getFolderName(folderId);
        setCurrentNote(prev => ({
            ...prev,
            folderId: folderId,
            folderName: newFolderName
        }));
        toast.success(t('notes.actions.movedTo', { folder: newFolderName }));
    } catch (error) {
        console.error('Error moving note:', error);
        toast.error(t('notes.detail.failedToMoveNote'));
    }
  };

  const handleTitleSave = async (newTitle) => {
    try {
      const noteRef = doc(db, 'notes', currentNote.id);
      await updateDoc(noteRef, {
        title: newTitle,
        lastModified: serverTimestamp()
      });

      setCurrentNote(prev => ({
        ...prev,
        title: newTitle
      }));
    } catch (error) {
      console.error('Error updating title:', error);
      toast.error(t('notes.detail.failedToUpdateTitle'));
      throw error; // Re-throw to let InlineEdit know it failed
    }
  };

  const handleSummaryShortSave = async (newValue) => {
    try {
      const noteRef = doc(db, 'notes', currentNote.id);
      await updateDoc(noteRef, {
        summary_short: newValue,
        lastModified: serverTimestamp()
      });
      setCurrentNote(prev => ({ ...prev, summary_short: newValue }));
    } catch (error) {
      console.error('Error updating summary_short:', error);
      toast.error('Failed to update summary');
      throw error;
    }
  };

  const handleInsightsSave = async (newValue) => {
    try {
      const noteRef = doc(db, 'notes', currentNote.id);
      await updateDoc(noteRef, {
        insights: newValue,
        lastModified: serverTimestamp()
      });
      setCurrentNote(prev => ({ ...prev, insights: newValue }));
    } catch (error) {
      console.error('Error updating insights:', error);
      toast.error(t('notes.detail.failedToUpdateInsights'));
      throw error;
    }
  };

  const handleSummarySave = async (newValue) => {
    try {
      const noteRef = doc(db, 'notes', currentNote.id);
      await updateDoc(noteRef, {
        summary: newValue,
        lastModified: serverTimestamp()
      });
      setCurrentNote(prev => ({ ...prev, summary: newValue }));
    } catch (error) {
      console.error('Error updating summary:', error);
      toast.error('Failed to update summary');
      throw error;
    }
  };

  const handleTranscriptSave = async (newValue) => {
    try {
      const noteRef = doc(db, 'notes', currentNote.id);
      await updateDoc(noteRef, {
        transcript: newValue,
        lastModified: serverTimestamp()
      });
      setCurrentNote(prev => ({ ...prev, transcript: newValue }));
    } catch (error) {
      console.error('Error updating transcript:', error);
      toast.error(t('notes.detail.failedToUpdateTranscript'));
      throw error;
    }
  };

  // Context menu handlers
  const handleMenuClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      visible: true,
      x: rect.left,
      y: rect.bottom + 5,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  const togglePinStatus = async (note) => {
    const newStatus = !note.isPinned;
    
    // Optimistic update
    setCurrentNote(prev => ({ ...prev, isPinned: newStatus }));

    try {
      const noteRef = doc(db, 'notes', note.id);
      await updateDoc(noteRef, {
        isPinned: newStatus
      });
      toast.success(newStatus ? t('notes.actions.notePinned') : t('notes.actions.noteUnpinned'));
      
      // Track analytics
      if (newStatus) {
        trackNotePinned(note.id);
      } else {
        trackNoteUnpinned(note.id);
      }
    } catch (error) {
      console.error('Pin update failed:', error);
      toast.error(t('notes.actions.failedToUpdateNote'));
      
      // Revert optimistic update
      setCurrentNote(prev => ({ ...prev, isPinned: !newStatus }));
    }
  };

  const handleMoveNote = () => {
    setIsFolderModalOpen(true);
  };

  const handleChangeNoteType = async (note, newType) => {
    // Optimistic update
    setCurrentNote(prev => ({ ...prev, noteType: newType }));

    try {
      const noteRef = doc(db, 'notes', note.id);
      await updateDoc(noteRef, {
        noteType: newType,
        lastModified: serverTimestamp()
      });
      toast.success(t('notes.actions.noteTypeUpdated'));
    } catch (error) {
      console.error('Error updating note type:', error);
      toast.error(t('notes.actions.failedToUpdateNoteType'));
      // Revert optimistic update
      setCurrentNote(prev => ({ ...prev, noteType: note.noteType }));
    }
  };

  const handleShareNote = () => {
    setIsShareModalOpen(true);
    trackNoteShare(currentNote.id);
  };

  const handlePrintNote = async (note) => {
    try {
      const getNoteHtml = httpsCallable(functions, 'getNoteHtml');
      const result = await getNoteHtml({ docId: note.id });
      
      const { html } = result.data;
      
      // Open print window with generated HTML
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      
      // Track analytics
      trackNotePrint(note.id);
    } catch (error) {
      console.error('Error printing note:', error);
      toast.error('Error preparing note for printing');
    }
  };

  const handleResendEmail = async (note, emailAddress = null) => {
    const targetEmail = emailAddress || userData?.sendEmailAddress || userData?.email || 'primary email';
    const confirmed = window.confirm(t('notes.actions.resendNoteTo', { email: emailAddress || targetEmail }));
    
    if (!confirmed) return;
    
    try {
      const processNote = httpsCallable(functions, 'processNote');
      await processNote({
        docId: note.id,
        processTranscript: false,
        processSummary: false,
        processMetadata: false,
        processSendEmail: true,
        secondarySendEmail: emailAddress || undefined
      });
      
      toast.success(t('notes.actions.noteResentSuccessfully'));
      
      // Track analytics
      trackNoteResend(note.id);
    } catch (error) {
      console.error('Resend failed:', error);
      toast.error(t('notes.actions.failedToResend'));
    }
  };

  const handleResummarizeNote = async (note) => {
    const confirmed = window.confirm(t('notes.actions.confirmResummarize'));
    if (!confirmed) return;

    setIsProcessing(true);
    setCurrentNote(prev => ({
      ...prev,
      isProcessing: true,
      processingStatus: 21,
      processingProgress: 20
    }));

    try {
      const processNote = httpsCallable(functions, 'processNote');
      const result = await processNote({
        docId: note.id,
        processTranscript: false,
        processSummary: true,
        processMetadata: true,
        processSendEmail: false
      });

      if (result.data && result.data.result === 'Ok') {
        toast.success(t('notes.actions.noteResummarizedSuccessfully'));
        trackNoteResummarize(note.id);
      } else {
        toast.error(t('notes.actions.failedToResummarize'));
      }
    } catch (error) {
      console.error('Resummarize failed:', error);
      toast.error(t('notes.actions.failedToResummarize'));
    } finally {
      setIsProcessing(false);
      setCurrentNote(prev => ({
        ...prev,
        isProcessing: false
      }));
    }
  };

  const handleRetranscribeNote = async (note) => {
    if (!note.recordingURL) {
      toast.error(t('notes.actions.noRecordingToRetranscribe'));
      return;
    }

    const confirmed = window.confirm(t('notes.actions.confirmRetranscribe'));
    if (!confirmed) return;

    setIsProcessing(true);
    setCurrentNote(prev => ({
      ...prev,
      isProcessing: true,
      processingStatus: 10,
      processingProgress: 10
    }));

    try {
      const processNote = httpsCallable(functions, 'processNote');
      const result = await processNote({
        docId: note.id,
        processTranscript: true,
        processSummary: false,
        processMetadata: false,
        processSendEmail: false
      });

      if (result.data && result.data.result === 'Ok') {
        toast.success(t('notes.actions.noteRetranscribedSuccessfully'));
        trackNoteRetranscript(note.id);
      } else {
        toast.error(t('notes.actions.failedToRetranscribe'));
      }
    } catch (error) {
      console.error('Retranscribe failed:', error);
      toast.error(t('notes.actions.failedToRetranscribe'));
    } finally {
      setIsProcessing(false);
      setCurrentNote(prev => ({
        ...prev,
        isProcessing: false
      }));
    }
  };

  const handleDeleteNote = async () => {
    const confirmDelete = window.confirm(t('notes.detail.confirmDelete'));
    if (!confirmDelete) return;

    try {
      const noteRef = doc(db, 'notes', currentNote.id);
      await updateDoc(noteRef, {
        isPendingtoDelete: true
      });
      
      toast.success(t('notes.detail.noteDeleted'));
      onDelete(currentNote.id);
      onClose();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(t('notes.actions.failedToDelete'));
    }
  };

  const handleRetry = async () => {
    setIsProcessing(true);
    try {
      const processTranscript = !currentNote.transcript;
      const processNote = httpsCallable(functions, 'processNote');
      const result = await processNote({
        docId: currentNote.id,
        processTranscript: processTranscript,
        processSummary: true,
        processMetadata: true,
        processSendEmail: true,
        noteData: currentNote,
      });

      if (result.data && result.data.result === 'Ok') {
        toast.success(t('notes.detail.noteReprocessedSuccessfully'));
      } else {
        toast.error(t('notes.detail.failedToReprocess'));
      }
    } catch (error) {
      console.error('Error retrying note processing:', error);
      toast.error(t('notes.detail.errorRetrying'));
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!currentNote) {
    return null;
  }

  const isError = currentNote.processingStatus === 400 || currentNote.processingStatus === 402;

  const dialogContent = (
    <div className="relative flex h-full flex-col overflow-y-auto p-5">
      {/* Header con botones */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-10">
        {/* Botón cerrar - siempre a la izquierda */}
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Close note detail"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        {/* Botón menú - siempre a la derecha */}
        <button
          onClick={handleMenuClick}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Open menu"
        >
          <EllipsisVerticalIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="text-center mb-6 pt-16 sm:pt-12">
        <p className="text-gray-500 text-sm mb-6">{formatDate(currentNote.creationDate)}</p>

        <div className="flex justify-center gap-2 mb-4 flex-wrap">
          <button 
              onClick={() => setIsFolderModalOpen(true)}
              className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-600 hover:bg-gray-200 transition-colors"
          >
              <FolderIcon className="w-4 h-4 mr-1.5 text-gray-500" />
              <span className="font-medium">{currentNote.folderName || t('common.folder')}</span>
              <ChevronRightIcon className="w-3 h-3 ml-1 text-gray-400" />
          </button>

          <button
              onClick={() => setIsContactPickerOpen(true)}
              className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-600 hover:bg-gray-200 transition-colors"
          >
              <UsersIcon className="w-4 h-4 mr-1.5 text-gray-500" />
              <span className="font-medium">
                  {Array.isArray(currentNote.contacts) && currentNote.contacts.length > 0 
                      ? `${currentNote.contacts.length} ${currentNote.contacts.length > 1 ? t('common.contacts') : t('common.contact')}` 
                      : t('notes.detail.addContacts')}
              </span>
              <ChevronRightIcon className="w-3 h-3 ml-1 text-gray-400" />
          </button>
        </div>

        <span
          className={`text-5xl my-6 block ${isError ? 'text-red-500' : 'text-blue-500'}`}
        >
          {isError ? '😱' : isProcessing ? '💃' : currentNote.emoji}
        </span>

        {isProcessing || isError ? (
          <h2 className="text-2xl font-bold mb-4 px-4">
            {isProcessing
              ? t('notes.detail.processingNote')
              : t('notes.detail.errorProcessingNote')}
          </h2>
        ) : (
          <div className="mb-4">
            <InlineEdit
              value={currentNote.title}
              onSave={handleTitleSave}
              placeholder={t('notes.detail.enterTitle')}
              className="text-2xl font-bold"
              textAlign="center"
              multiline={true}
            />
          </div>
        )}

        {!isError && !isProcessing && (
          <div className="mb-6">
            <ImageCarousel 
              noteId={currentNote.id}
              uid={currentNote.uid}
              images={currentNote.images}
              onImagesUpdate={handleImagesUpdate}
              onImageClick={handleImageClick}
            />
          </div>
        )}

        {!isError && currentNote.tags && currentNote.tags.length > 0 && (
          <div className="flex justify-center flex-wrap gap-2 mb-4">
            {currentNote.tags.map((tag, index) => (
              <span
                key={index}
                className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {!isError && Array.isArray(currentNote.contacts) && currentNote.contacts.length > 0 && (
           <div className="flex justify-center flex-wrap gap-2 mb-4">
              {currentNote.contacts.map((c) => (
                  <Link 
                      key={c.id}
                      to={`/contacts/${c.id}`}
                      className="inline-flex items-center bg-orange-50 text-orange-700 text-xs px-2 py-1 rounded border border-orange-100 hover:bg-orange-100 transition-colors"
                  >
                      <UserIcon className="w-3 h-3 mr-1" />
                      {c.contactName}
                  </Link>
              ))}
           </div>
        )}

        {isError && !isProcessing && (
          <div className="text-gray-500 p-4 rounded mb-4">
            {currentNote.processingLastError || t('notes.detail.unknownError')}
          </div>
        )}

        {isError && !isProcessing && (
          <div className="flex flex-col items-center">
            <button
              onClick={handleRetry}
              className="flex items-center justify-center my-6 px-4 py-2 bg-[#F07807] text-white rounded hover:bg-[#d66a05] transition-colors mb-4"
            >
               {t('common.retry')}
            </button>
            <button
              onClick={handleDeleteNote}
              className="px-4 py-2 my-4 bg-gray-100 text-red-500 rounded hover:bg-red-600 transition-colors"
            >
              Delete Note
            </button>
          </div>
        )}
      </div>

      {!isError && (
        <>
          <hr className="mb-6" />

          <div className="text-left">
            {currentNote.keywords && currentNote.keywords.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">{t('notes.detail.keywords')}</h3>
                <div className="flex flex-wrap gap-2">
                  {currentNote.keywords.map((keyword, index) => (
                    <span key={index} className="bg-gray-200 text-gray-800 text-sm px-2 py-1 rounded">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {currentNote.insights && (
              <div className="mb-4 bg-gray-100 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-2">{t('notes.detail.insights')}</h3>
                <MarkdownEditableField
                  value={currentNote.insights}
                  onSave={handleInsightsSave}
                  minHeight={200}
                  placeholder={t('notes.detail.noInsightsAvailable')}
                  modalTitle={t('notes.detail.editInsights')}
                />
              </div>
            )}

            {currentNote.links && currentNote.links.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-500 mb-4">{t('notes.detail.links')}</h3>
                <div className="flex flex-col gap-3">
                  {currentNote.links.map((link, index) => (
                    <LinkPreview 
                      key={index} 
                      url={link.url} 
                      title={link.title} 
                      imageURL={link.imageURL}
                      clickable={true}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4 max-w-none">
              <h3 className="text-sm2 font-bold text-gray-500 mb-4">{t('notes.detail.summary')}</h3>
              <MarkdownEditableField
                value={currentNote.summary_short}
                onSave={handleSummaryShortSave}
                minHeight={150}
                placeholder={t('notes.detail.noSummaryAvailable')}
                modalTitle={t('notes.detail.editSummary')}
              />
            </div>

            <div className="mb-4">
              <div className="flex border-b">
                <button
                  className={`py-2 px-4 ${activeTab === 'note' ? 'border-b-2 border-blue-500' : ''}`}
                  onClick={() => setActiveTab('note')}
                >
                  {t('common.note')}
                </button>
                <button
                  className={`py-2 px-4 ${activeTab === 'transcript' ? 'border-b-2 border-blue-500' : ''}`}
                  onClick={() => setActiveTab('transcript')}
                >
                  {t('notes.detail.transcript')}
                </button>
              </div>
              <div className="mt-4">
                {activeTab === 'note' && (
                  <MarkdownEditableField
                    value={currentNote.summary}
                    onSave={handleSummarySave}
                    minHeight={400}
                    placeholder={t('notes.detail.noNoteAvailable')}
                    modalTitle={t('notes.detail.editNote')}
                  />
                )}
                {activeTab === 'transcript' && (
                  <MarkdownEditableField
                    value={currentNote.transcript}
                    onSave={handleTranscriptSave}
                    minHeight={400}
                    placeholder={t('notes.detail.noTranscriptAvailable')}
                    modalTitle={t('notes.detail.editTranscript')}
                  />
                )}
              </div>
            </div>

            {currentNote.recordingURL && (
              <div className="mt-8">
                <h3 className="text-sm2 font-bold text-gray-500">{t('notes.detail.recording')}</h3>
                <div className="audio-player">
                  <audio controls src={currentNote.recordingURL} className="w-full">
                    {t('notes.detail.audioNotSupported')}
                  </audio>
                </div>
              </div>
            )}

            <div className="mt-12 pb-8 text-center">

            </div>
          </div>
        </>
      )}
    </div>
  );

  const modalWrapper = (
    <div
      className={`fixed inset-0 bg-gray-600 bg-opacity-50 z-[60] flex items-start justify-center ${isDesktop ? 'note-detail-modal-backdrop-enter' : ''}`}
      role="dialog"
      aria-modal="true"
    >
      <div className={`relative mx-auto w-full h-full sm:h-[90vh] sm:max-w-2xl sm:rounded-md sm:my-8 bg-white shadow-lg border overflow-hidden flex flex-col ${isDesktop ? 'note-detail-modal-enter' : ''}`}>
        {dialogContent}
      </div>
    </div>
  );

  const modalWideWrapper = (
    <div
      className={`fixed inset-0 bg-gray-600 bg-opacity-50 z-[60] flex items-start justify-center ${isDesktop ? 'note-detail-modal-backdrop-enter' : ''}`}
      role="dialog"
      aria-modal="true"
    >
      <div className={`relative mx-auto w-full h-full sm:h-[90vh] sm:max-w-5xl sm:rounded-md sm:my-8 bg-white shadow-lg border overflow-hidden flex flex-col ${isDesktop ? 'note-detail-modal-enter' : ''}`}>
        {dialogContent}
      </div>
    </div>
  );

  const sidebarWrapper = (
    <aside
      className={`h-full w-full bg-white border-l border-gray-200 shadow-inner flex flex-col ${isDesktop ? 'note-detail-sidebar-enter' : ''}`}
      role="region"
      aria-label="Note details"
    >
      {dialogContent}
    </aside>
  );

  const getWrapper = () => {
    if (variant === 'sidebar') return sidebarWrapper;
    if (variant === 'modalWide') return modalWideWrapper;
    return modalWrapper;
  };

  return (
    <>
      {getWrapper()}

      {previewImages && (
        <ImagePreviewModal
          images={previewImages}
          initialIndex={previewIndex}
          uid={currentNote.uid}
          onClose={handleClosePreview}
        />
      )}

      <FolderSelectionModal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSelectFolder={handleFolderSelect}
        currentFolderId={currentNote.folderId}
      />

      <ContactPickerModal
        isOpen={isContactPickerOpen}
        onClose={() => setIsContactPickerOpen(false)}
        onSelectContacts={handleContactsUpdate}
        initialSelectedContactIds={currentNote.contactIds || []}
      />

      {/* Context Menu */}
      <NoteContextMenu
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
        note={currentNote}
        onClose={closeContextMenu}
        onPin={togglePinStatus}
        onMove={handleMoveNote}
        onChangeType={handleChangeNoteType}
        onShare={handleShareNote}
        onPrint={handlePrintNote}
        onResend={handleResendEmail}
        onResummarize={handleResummarizeNote}
        onRetranscribe={handleRetranscribeNote}
        onDelete={handleDeleteNote}
        secondaryEmails={userData?.secondarySendEmails || []}
        primaryEmail={userData?.sendEmailAddress || userData?.email || t('common.primaryEmail')}
      />

      {/* Share Modal */}
      <NoteShareModal
        isOpen={isShareModalOpen}
        note={currentNote}
        onClose={() => setIsShareModalOpen(false)}
      />
    </>
  );
}

NoteDetail.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.string.isRequired,
    emoji: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    creationDate: PropTypes.object.isRequired,
    summary: PropTypes.string,
    summary_short: PropTypes.string,
    transcript: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    keywords: PropTypes.arrayOf(PropTypes.string),
    insights: PropTypes.string,
    recordingURL: PropTypes.string,
    processingStatus: PropTypes.number,
    processingLastError: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    uid: PropTypes.string, // Add uid prop validation
    folderId: PropTypes.string,
    folderName: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['modal', 'sidebar', 'modalWide']),
};

export default NoteDetail;
